'use client';

import type { EraProduct, UserRole } from '@/hooks/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { debounce } from 'radash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/era-ui';
import { trpc as api } from '@/libs/trpc';
import { SignUpFormWrapper } from '../../app/[locale]/(auth)/(center)/auth/FormWrapper';
import { ArrowLeft } from '../../app/[locale]/(auth)/(center)/auth/Images';
import { Form, FormControl, FormField, FormItem, FormLabel } from './legacy/Form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './legacy/InputOTP';

export const TokenSent = ({ email, setShowLinkSent, redirectTo }: { email: string; setShowLinkSent: (show: boolean) => void; redirectTo: string }) => {
  const t = useTranslations('Auth');
  const [authLoading, setAuthLoading] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const role = searchParams.get('role')?.toLowerCase().replace(/[^0-9a-z]/gi, '') as UserRole || 'candidate';
  const product = searchParams.get('product') as EraProduct || null;
  const referralCode = searchParams.get('referralCode');

  const verifyOtp = api.auth.verifyOtp.useMutation();

  const inputTokenFormSchema = z.object({
    token: z.string().length(6),
  });

  const form = useForm<z.infer<typeof inputTokenFormSchema>>({
    resolver: zodResolver(inputTokenFormSchema),
    defaultValues: {
      token: '',
    },
  });

  useEffect(() => {
    const token = form.watch('token');
    if (token?.length === 6) {
      setAuthLoading(true);
      onSubmit({ token });
    }
  }, [form.watch('token')]);

  // For when the user inputs the OTP token they received in their email.
  const onSubmit = async (data: z.infer<typeof inputTokenFormSchema>) => {
    if (authLoading && form.formState.isSubmitting) {
      return;
    }

    const invitedCustomInterviewsTemplateId = document.cookie
      .split('; ')
      .find(row => row.startsWith('invitedCustomInterviewsTemplateId='))
      ?.split('=')[1];
    console.info('invitedCustomInterviewsTemplateId=', invitedCustomInterviewsTemplateId);

    // if the user has a custom interview template id, we need to redirect to the custom interview template
    const redirectToPath = invitedCustomInterviewsTemplateId
      ? `/invite/custom-interviews-templates/${invitedCustomInterviewsTemplateId}?utm_source=email&utm_medium=campaign&utm_campaign=ai_tutor&useThankYouPage=true`
      : redirectTo;

    setAuthLoading(true);
    try {
      const result = await verifyOtp.mutateAsync({
        email,
        token: data.token,
        role,
        product: product || 'interviewer',
        redirectTo: redirectToPath,
        referralCode: referralCode ?? undefined,
      });
      router.push(result.next);
    } catch (error) {
      if (error instanceof Error && error.message === 'otp_expired') {
        toast.error(t('magicLinkSentV2.otp_expired'));
      } else {
        console.error('OTP verification error:', error);
        toast.error(t('magicLinkSentV2.error'));
      }
      debounce({ delay: 500 }, () => setAuthLoading(false));
    }
  };

  return (
    <SignUpFormWrapper
      header={(
        <div className="flex flex-row gap-4 w-full">
          <Button
            variant="secondary"
            onClick={() => setShowLinkSent(false)}
            className="border-none"
          >
            <ArrowLeft />
          </Button>
          <div className="flex flex-col gap-1">
            <h2 className="text-center text-header-md flex items-center">
              {t('magicLinkSentV2.title')}
            </h2>
            <p className="-mt-0 self-stretch text-center text-body-md text-black [font-feature-settings:'ss03'_on]">
              {t('magicLinkSentV2.description')}
            </p>
          </div>
        </div>
      )}
      className="w-full h-full"
    >
      <Form {...form}>
        <div className="w-full flex justify-center h-full">
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2 h-full items-center self-stretch px-2 w-full">
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem className="w-full flex flex-col">
                  <FormLabel>
                    {t('magicLinkSentV2.token')}
                  </FormLabel>
                  <FormControl>
                    <InputOTP maxLength={6} {...field} className="w-full flex flex-row" autoComplete="off">
                      <InputOTPGroup className="w-full flex flex-row">
                        <InputOTPSlot index={0} className="w-full flex-1 h-10" />
                        <InputOTPSlot index={1} className="w-full flex-1 h-10" />
                        <InputOTPSlot index={2} className="w-full flex-1 h-10" />
                        <InputOTPSlot index={3} className="w-full flex-1 h-10" />
                        <InputOTPSlot index={4} className="w-full flex-1 h-10" />
                        <InputOTPSlot index={5} className="w-full flex-1 h-10" />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="w-full flex justify-end mt-2">
              <Button type="submit" disabled={authLoading} className="w-full">
                {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('magicLinkSentV2.submit')}
              </Button>
            </div>
          </form>
        </div>
      </Form>
    </SignUpFormWrapper>
  );
};
